import {
  CenterModalPanel,
  type PanelPlacement,
  type PanelPresenter,
  SideSheetPanel,
  presenterKindForPlacement,
} from './panel-presenter'
import {applyPanelCornerRadius} from './sheet-shell-shared'
import {Shell, type ShellOptions} from './shell-base'
import {ShellStyles} from './shell-styles'
import type {AnagramMessage, EnvironmentConfig, ExternalTriggerConfig, ShellConfig} from './types'

declare const __BITS_CSS__: string
const BitsStyles: string = typeof __BITS_CSS__ !== 'undefined' ? __BITS_CSS__ : ''

export class ExternalTriggerShell extends Shell {
  private shellConfig: ExternalTriggerConfig
  private host: HTMLDivElement | null = null
  private root: HTMLDivElement | null = null
  private presenter!: PanelPresenter
  private currentIframe: HTMLIFrameElement | null = null
  private currentIframeContextKey: string | null = null
  private isOpen = false
  private isIframeReady = false
  private hasAttachedFontStylesheet = false
  private viewportResizeListener: (() => void) | null = null

  constructor(
    config: EnvironmentConfig,
    experienceId: string,
    shellConfig: ExternalTriggerConfig,
    options?: ShellOptions
  ) {
    super(config, experienceId, options)
    this.shellConfig = shellConfig
  }

  mount(): void {
    const fontFamily = this.getShellFontStack(this.shellConfig.fontFamily)

    this.host = document.createElement('div')
    this.host.dataset.anagramShell = 'external-trigger'
    this.styleAsViewportOverlay(this.host)
    document.body.appendChild(this.host)

    const shadow = this.host.attachShadow({mode: 'open'})

    const style = document.createElement('style')
    style.textContent = ShellStyles + BitsStyles
    shadow.appendChild(style)

    this.root = document.createElement('div')
    this.root.className = 'anagram-root'
    this.root.style.fontFamily = fontFamily
    applyPanelCornerRadius(this.root, this.shellConfig.theme['--radius'])
    shadow.appendChild(this.root)

    this.presenter = this.createPresenter(this.shellConfig.panelLocation)
    this.presenter.mount(this.root)

    this.startListeningForIframeMessages()
  }

  openPanel(): void {
    if (this.isOpen) {
      return
    }

    this.isOpen = true
    // Fonts and viewport tracking are deferred to first open: unlike launcher shells,
    // this shell renders nothing until the merchant's own button fires an event.
    this.ensureFontStylesheet()
    this.syncViewportListener()
    const {parentUrl, pageTitle} = this.getLiveIframePageContext()
    this.ensureIframe(parentUrl, pageTitle)

    if (this.isIframeReady) {
      this.presenter.showReady()
    }

    this.presenter.open()
  }

  private ensureFontStylesheet(): void {
    if (this.hasAttachedFontStylesheet || !this.host?.shadowRoot) {
      return
    }
    this.hasAttachedFontStylesheet = true
    this.attachFontStylesheet(this.host.shadowRoot, this.shellConfig.fontFamily)
  }

  override applyPreviewShellConfig(config: ShellConfig): boolean {
    if (config.formFactor !== 'external-trigger' || !this.root) {
      return false
    }

    this.shellConfig = config
    this.updateActivePreviewConfig(config)
    applyPanelCornerRadius(this.root, this.shellConfig.theme['--radius'])
    this.swapPresenterIfNeeded(this.shellConfig.panelLocation)
    this.presenter.applyPlacement(this.shellConfig.panelLocation)
    this.presenter.setSpinnerColor(this.shellConfig.accentColor)

    if (this.isOpen) {
      // The iframe context key includes the serialized preview config, so
      // `ensureIframe` reloads only when the config actually changed.
      const {parentUrl, pageTitle} = this.getLiveIframePageContext()
      this.ensureIframe(parentUrl, pageTitle)
    }

    return true
  }

  override destroy(): void {
    this.teardownViewportListener()
    this.presenter?.destroy()
    this.currentIframe?.remove()
    this.currentIframe = null
    this.currentIframeContextKey = null
    this.isOpen = false
    this.isIframeReady = false
    this.host?.remove()
    this.host = null
    this.root = null
    super.destroy()
  }

  protected override ownsIframeMessageSource(source: MessageEventSource): boolean {
    return source === this.currentIframe?.contentWindow
  }

  protected onIframeReady(): void {
    this.isIframeReady = true
    this.presenter.showReady()
  }

  protected handleShellSpecificIframeMessage(msg: AnagramMessage): void {
    switch (msg.type) {
      case 'ANAGRAM_CLOSE_REQUESTED': {
        this.closePanel()
        break
      }
      case 'ANAGRAM_RESIZE': {
        const nextHeight = Number(msg.height)
        if (!Number.isNaN(nextHeight) && nextHeight > 0) {
          this.presenter.setBodyHeight(nextHeight)
        }
        break
      }
      case 'ANAGRAM_START_OVER': {
        this.presenter.resetBodyHeight()
        break
      }
    }
  }

  private createPresenter(placement: PanelPlacement): PanelPresenter {
    const callbacks = {
      onDismissRequested: () => this.closePanel(),
      onClosed: () => {
        this.isOpen = false
      },
    }

    if (presenterKindForPlacement(placement) === 'center-modal') {
      return new CenterModalPanel({
        fontFamily: this.getShellFontStack(this.shellConfig.fontFamily),
        theme: this.shellConfig.theme,
        spinnerColor: this.shellConfig.accentColor,
        callbacks,
      })
    }

    return new SideSheetPanel({
      fontFamily: this.getShellFontStack(this.shellConfig.fontFamily),
      placement,
      mobileDrawer: false,
      spinnerColor: this.shellConfig.accentColor,
      callbacks,
    })
  }

  private swapPresenterIfNeeded(placement: PanelPlacement): void {
    if (!this.root || presenterKindForPlacement(placement) === this.presenter.kind) {
      return
    }

    const wasOpen = this.isOpen
    this.presenter.destroy()
    this.currentIframe = null
    this.currentIframeContextKey = null
    this.isIframeReady = false
    this.isOpen = false

    this.presenter = this.createPresenter(placement)
    this.presenter.mount(this.root)
    this.syncViewportListener()

    if (wasOpen) {
      this.openPanel()
    }
  }

  private closePanel(): void {
    if (!this.isOpen) {
      return
    }

    // Unlike sibling shells (which clear isOpen in the presenter's onClosed callback),
    // clear it eagerly so a merchant `anagram:open` fired during the ~500ms close
    // transition reopens the panel instead of being swallowed.
    this.isOpen = false
    this.presenter.close()
  }

  private ensureIframe(parentUrl: string, pageTitle: string): void {
    const contextKey = this.buildIframeContextKey(parentUrl, pageTitle)
    if (this.currentIframe && this.currentIframeContextKey === contextKey) {
      return
    }

    this.currentIframe?.remove()

    const iframeUrl = this.createIframeUrl('sheet')
    iframeUrl.searchParams.append('parentUrl', parentUrl)
    if (pageTitle) {
      iframeUrl.searchParams.append('pageTitle', pageTitle)
    }

    this.currentIframe = this.createIframe(iframeUrl)
    this.currentIframeContextKey = contextKey
    this.isIframeReady = false
    this.presenter.showLoading()
    this.presenter.iframeContainer.appendChild(this.currentIframe)
  }

  private syncViewportListener(): void {
    if (this.presenter.tracksViewport && !this.viewportResizeListener) {
      this.viewportResizeListener = () => this.presenter.syncViewport()
      window.addEventListener('resize', this.viewportResizeListener)
      window.visualViewport?.addEventListener('resize', this.viewportResizeListener, {passive: true})
      window.visualViewport?.addEventListener('scroll', this.viewportResizeListener, {passive: true})
      return
    }

    if (!this.presenter.tracksViewport && this.viewportResizeListener) {
      this.teardownViewportListener()
    }
  }

  private teardownViewportListener(): void {
    if (!this.viewportResizeListener) {
      return
    }
    window.removeEventListener('resize', this.viewportResizeListener)
    window.visualViewport?.removeEventListener('resize', this.viewportResizeListener)
    window.visualViewport?.removeEventListener('scroll', this.viewportResizeListener)
    this.viewportResizeListener = null
  }
}
